import { useCallback, useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UploadDocumentsProps } from "@/components/UploadDocuments";
import { useTRPC } from "@/trpc";
import {
  vaultFilesQueryOptions,
  vaultCandidatesQueryKey,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import { ProviderCredentialUpload } from "./credential-vault-edit-modal/provider-credential-upload";
import { ProviderImportCatalogButton } from "./provider-imports/provider-import-catalog-button";
import { ProviderImportContext } from "../lib/provider-import-context";

export function CredentialVaultUpload({
  scope,
  disabled,
  onUploadStateChange,
}: {
  scope: CredentialVaultScope;
  disabled: boolean;
  onUploadStateChange: NonNullable<UploadDocumentsProps["onUploadStateChange"]>;
}) {
  const trpc = useTRPC();
  const imports = useContext(ProviderImportContext);
  const providerImports =
    scope.kind === "organization" && !scope.credentialViewId ? imports : null;
  const queryClient = useQueryClient();
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    pendingFileCount: 0,
  });
  // The uploader's state-notification effect depends on this callback. Keep it
  // stable so storing its snapshot does not trigger another notification.
  const handleUploadStateChange = useCallback(
    (state: typeof uploadState) => {
      setUploadState(state);
      onUploadStateChange(state);
    },
    [onUploadStateChange],
  );

  async function refreshFiles() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: vaultFilesQueryOptions(trpc, scope).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: vaultCandidatesQueryKey(trpc, scope),
      }),
    ]);
  }

  return (
    <ProviderCredentialUpload
      compact
      userId={scope.kind === "provider" ? scope.userId : scope.providerUserId}
      expectedAffiliationId={
        scope.kind === "organization" ? scope.expectedAffiliationId : undefined
      }
      organizationBusinessUnitId={
        scope.kind === "organization"
          ? scope.organizationBusinessUnitId
          : undefined
      }
      isEditing={false}
      disabled={disabled || !!providerImports?.isOpen}
      actions={
        providerImports ? (
          <ProviderImportCatalogButton
            compact
            disabled={
              disabled ||
              uploadState.isUploading ||
              uploadState.pendingFileCount > 0
            }
          />
        ) : undefined
      }
      review={{
        isUploadOpen,
        setUploadOpen,
        uploadState,
        setUploadState: handleUploadStateChange,
        filesUploaded: refreshFiles,
        query: { refetch: refreshFiles },
      }}
    />
  );
}
